import { Box, styled } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    PreviewVolume,
    RoiPreviewRenderer,
} from '~/engine/tool/actions/contouring-actions/RoiPreviewRenderer';
import { OrbitView } from '~/engine/util/geometry/camera';
import { useCommittedRoiVolumes } from '~/hooks/viewer/useCommittedRoiVolumes';
import {
    RoiPreviewSubject,
    useRoiPreviewDrawableRoiIds,
} from './useRoiPreviewSubject';

const MAX_DEVICE_PIXEL_RATIO = 2;
/**
 * Most device pixels the drawing buffer takes per side. The scene is
 * ray-marched per buffer pixel on every orbit frame, so a pane fitted to a
 * large box is drawn at this size and scaled up, rather than marched at the
 * box's full resolution.
 */
const MAX_BUFFER_SIDE_PX = 960;
const ORBIT_SENSITIVITY_DEG_PER_PX = 0.5;
const ROLL_SENSITIVITY_DEG_PER_PX = 0.5;
const ZOOM_STEP = 1.1;
/** Closest and furthest the wheel may take the camera, as multiples of the
 *  distance that frames the structures. Closer fills the pane with the
 *  interior of a surface; further shrinks them below a few pixels. */
const MIN_ZOOM_FACTOR = 0.2;
const MAX_ZOOM_FACTOR = 5;
const INITIAL_AZIMUTH_DEG = 35;
const INITIAL_ELEVATION_DEG = 20;
/** The pose a pane starts in when none is handed to it. */
const DEFAULT_VIEW: OrbitView = {
    azimuthDeg: INITIAL_AZIMUTH_DEG,
    elevationDeg: INITIAL_ELEVATION_DEG,
    distanceMm: 1,
};

/**
 * States how much of the selection is drawn, and only when that is some of
 * it but not all. The cap exists to bound GPU readback cost, but a preview
 * that quietly showed 8 of 23 structures would misrepresent the selection;
 * "0 of 1" for a structure with no contours yet would read as a failure.
 */
const ShownCount = styled(Box)`
    position: absolute;
    right: 3px;
    bottom: 2px;
    padding: 0 3px;
    border-radius: 2px;
    font-size: 10px;
    /* Fixed, not theme.palette.text.secondary: the pane background is a fixed
       near-black in every theme, so a theme-following colour is dark-on-dark
       in the light theme, and this text is what keeps a bounded preview from
       reading as the whole selection, so it must be legible in both. */
    color: rgba(255, 255, 255, 0.85);
    background: rgba(16, 18, 22, 0.75);
    pointer-events: none;
`;

export interface RoiPreviewPaneProps extends RoiPreviewSubject {
    /**
     * A shape that is not part of any structure yet, drawn among the
     * selected ones; none by default.
     */
    readonly uncommittedVolume?: PreviewVolume | null;
    /** The side of the square scene, CSS px. */
    readonly sizePx: number;
    /**
     * The camera pose to start in, in place of the default one; a host
     * that remounts the pane hands back the pose it was last told of, so
     * the user's view survives the remount.
     */
    readonly initialView?: OrbitView;
    /** Told the pose after every orbit, roll and zoom. */
    readonly onViewChange?: (view: OrbitView) => void;
}

/**
 * A square 3D preview of the selected ROIs, rendered by ray-marching their
 * signed-distance fields in an isolated WebGL2 context. It helps the user
 * spot gaps, spurs, and disconnected fragments without scrolling every
 * slice. This is a read-only, non-clinical visualization aid: it never
 * mutates contour data.
 *
 * Everything it draws is handed in; it reads no viewer state of its own. It
 * shows whatever is selected, one ROI or a bulk selection, following the
 * selection as it changes, and renders nothing while nothing is drawable.
 * While mounted it updates live as the user edits (throttled to <= 4 Hz),
 * keeping the current camera orientation across edits; while nothing is
 * drawable the WebGL context and the subscriptions are torn down so it does
 * no work.
 *
 * It also draws an uncommitted shape handed to it, in preview green, depth
 * tested against the committed structures so it is judged in context.
 *
 * Drag orbits, Alt-drag rolls, the wheel zooms. Pointer and wheel events
 * stop at the pane, so a host laid over a viewport never sees them as its
 * own. The pane is square: the orbit camera frames the selection's bounding
 * box from an arbitrary direction, so no one axis sets the aspect.
 */
export default function RoiPreviewPane({
    environment,
    structureSetUid,
    setRoiOrder,
    selectedRoiId,
    multiSelectedRoiIds,
    uncommittedVolume = null,
    sizePx,
    initialView,
    onViewChange,
}: RoiPreviewPaneProps) {
    const intl = useIntl();

    // Which ROIs the preview is FOR. Cheap and purely derived from the
    // selection: an ROI that is not loaded, or a point ROI, has no surface to
    // march and is dropped, so it neither consumes the slice budget nor counts
    // among the structures the pane reports against.
    const drawableRoiIds = useRoiPreviewDrawableRoiIds(
        environment,
        setRoiOrder,
        selectedRoiId,
        multiSelectedRoiIds
    );

    // Which of those it actually DRAWS, as volumes ready to upload, kept
    // current as the user edits.
    const committed = useCommittedRoiVolumes(
        environment,
        structureSetUid,
        drawableRoiIds
    );

    const rootRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const rendererRef = useRef<RoiPreviewRenderer | null>(null);
    const viewRef = useRef<OrbitView>(initialView ?? DEFAULT_VIEW);
    // A pane handed a pose keeps it through the first load; one without
    // frames the selection itself.
    const hasInitialViewRef = useRef(initialView !== undefined);
    const onViewChangeRef = useRef(onViewChange);
    onViewChangeRef.current = onViewChange;
    const draggingRef = useRef(false);
    const lastPointerRef = useRef({ x: 0, y: 0 });
    // Only a selection change reframes; an edit keeps the user's camera.
    const selectionTokenRef = useRef(committed.selectionToken);

    // Keyed on what the selection COULD draw, not on what it currently draws:
    // an ROI selected before it has any contours keeps its scene, empty, for
    // the first stroke to appear in. A point ROI, or nothing selected, still
    // hides it.
    const isVisible = drawableRoiIds.length > 0;

    const volumes = useMemo(
        (): readonly PreviewVolume[] =>
            uncommittedVolume
                ? [...committed.volumes, uncommittedVolume]
                : committed.volumes,
        [committed.volumes, uncommittedVolume]
    );

    const renderNow = useCallback((): void => {
        rendererRef.current?.render(viewRef.current);
    }, []);

    /** Draws the pose the user has just changed, and tells the host of it. */
    const renderChangedView = useCallback((): void => {
        renderNow();
        onViewChangeRef.current?.({ ...viewRef.current });
    }, [renderNow]);

    // Create/dispose the isolated renderer alongside the canvas lifecycle.
    // Deliberately NOT keyed on the size: a resize must not tear down and
    // rebuild a WebGL2 context on every pointer move. The effect below
    // re-sizes the existing context's drawing buffer instead.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!isVisible || !canvas) {
            return;
        }
        let renderer: RoiPreviewRenderer | null = null;
        try {
            renderer = new RoiPreviewRenderer(canvas);
        } catch (error) {
            // WebGL2 unavailable: fail quietly, this is a non-clinical aid.
            console.warn('RoiPreviewPane: renderer init failed', error);
            return;
        }
        rendererRef.current = renderer;

        return () => {
            renderer?.dispose();
            rendererRef.current = null;
        };
    }, [isVisible]);

    // Match the drawing buffer to the pane size, up to the buffer cap. Runs
    // after the effect above on first show (effects fire in declaration
    // order), so the initial render already has a sized buffer.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!isVisible || !canvas) {
            return;
        }
        const dpr = Math.min(
            window.devicePixelRatio || 1,
            MAX_DEVICE_PIXEL_RATIO
        );
        const bufferSidePx = Math.min(sizePx * dpr, MAX_BUFFER_SIDE_PX);
        canvas.width = bufferSidePx;
        canvas.height = bufferSidePx;
        renderNow();
    }, [isVisible, sizePx, renderNow]);

    // Upload what is to be drawn and render it. The camera is reframed only
    // for a new selection or on the first load, so live edits never reset an
    // orientation the user has chosen.
    useEffect(() => {
        const renderer = rendererRef.current;
        // The volumes of a selection arrive a render after the selection
        // does. Until the first has been resolved there is nothing to judge
        // a framing by, and an uncommitted shape loaded on its own would
        // spend a pose handed in by the host before the selection is drawn.
        if (!renderer || committed.selectionToken === 0) {
            return;
        }
        const resetView =
            selectionTokenRef.current !== committed.selectionToken;
        selectionTokenRef.current = committed.selectionToken;

        // Uploading can throw: setVolumes refuses a malformed volume or an
        // allocation the GPU will not make (a structure past
        // MAX_3D_TEXTURE_SIZE, or out of video memory). Caught for the same
        // reason the renderer's construction is: this is a non-clinical aid,
        // so it degrades to an empty pane rather than throwing out of an
        // effect, where the nearest boundary is the route's, taking the
        // whole viewer with it.
        try {
            // An empty list clears the pane, which is what an ROI selected
            // before it has any contours should show. A pose handed in by
            // the host stands in for the first framing.
            const shouldFrame =
                volumes.length > 0 &&
                (resetView || !renderer.hasVolumeLoaded) &&
                !hasInitialViewRef.current;
            if (volumes.length > 0) {
                hasInitialViewRef.current = false;
            }
            renderer.setVolumes(volumes);
            if (shouldFrame) {
                // A framing is a pose change like any other: the host
                // remembers it, so a remount after a change of subject
                // does not draw the new subject at the old pose.
                viewRef.current = {
                    azimuthDeg: INITIAL_AZIMUTH_DEG,
                    elevationDeg: INITIAL_ELEVATION_DEG,
                    distanceMm: renderer.frameDistanceMm,
                    rollDeg: 0,
                };
                renderChangedView();
            } else {
                renderNow();
            }
        } catch (error) {
            console.warn('RoiPreviewPane: loading the preview failed', error);
            renderer.clearVolume();
            renderNow();
        }
    }, [
        isVisible,
        volumes,
        committed.selectionToken,
        renderNow,
        renderChangedView,
    ]);

    // Zoom. A native, non-passive listener on the pane's own root: the
    // event is default-prevented so nothing under the pane scrolls a slice,
    // and stopped so a host laid over a viewport never routes it there.
    useEffect(() => {
        const root = rootRef.current;
        if (!isVisible || !root) {
            return;
        }
        const handleWheel = (event: WheelEvent): void => {
            event.preventDefault();
            event.stopPropagation();
            const view = viewRef.current;
            // Holding Shift makes the browser deliver vertical scroll on the X
            // axis (deltaY becomes 0, deltaX carries the value), so fall back
            // to deltaX to keep zoom working with Shift held.
            const delta = event.deltaY || event.deltaX;
            // Bounded against the distance that frames the scene. Unbounded,
            // a sustained wheel drives the camera to zero or to infinity and
            // the surfaces simply vanish, indistinguishable, in a preview,
            // from the structures having no volume or the renderer having
            // failed.
            const framed = rendererRef.current?.frameDistanceMm ?? null;
            const next =
                view.distanceMm * (delta > 0 ? ZOOM_STEP : 1 / ZOOM_STEP);
            view.distanceMm =
                framed !== null
                    ? Math.min(
                          Math.max(next, framed * MIN_ZOOM_FACTOR),
                          framed * MAX_ZOOM_FACTOR
                      )
                    : next;
            renderChangedView();
        };
        root.addEventListener('wheel', handleWheel, { passive: false });
        return () => root.removeEventListener('wheel', handleWheel);
    }, [isVisible, renderChangedView]);

    const handlePointerDown = (
        event: React.PointerEvent<HTMLCanvasElement>
    ) => {
        event.stopPropagation();
        draggingRef.current = true;
        lastPointerRef.current = { x: event.clientX, y: event.clientY };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handlePointerMove = (
        event: React.PointerEvent<HTMLCanvasElement>
    ) => {
        event.stopPropagation();
        if (!draggingRef.current) {
            return;
        }
        const dx = event.clientX - lastPointerRef.current.x;
        const dy = event.clientY - lastPointerRef.current.y;
        lastPointerRef.current = { x: event.clientX, y: event.clientY };
        const view = viewRef.current;
        if (event.altKey) {
            // Alt-drag spins the ROI in place about the view axis (in-plane
            // roll), the third rotational DOF, instead of moving the eye to a
            // new vantage point. Reading altKey off the pointer event avoids
            // any keydown/keyup listeners or key-state to manage.
            view.rollDeg =
                (view.rollDeg ?? 0) + dx * ROLL_SENSITIVITY_DEG_PER_PX;
        } else {
            // Both axes orbit freely (unbounded) for a full 360° view of the
            // ROI. Both follow the cursor ("grab" feel): dragging right/down
            // turns the ROI so the surface tracks the pointer. Azimuth is
            // subtracted because increasing it rotates the eye the opposite way
            // around the vertical.
            view.azimuthDeg -= dx * ORBIT_SENSITIVITY_DEG_PER_PX;
            view.elevationDeg += dy * ORBIT_SENSITIVITY_DEG_PER_PX;
        }
        renderChangedView();
    };

    // Also bound to pointercancel: the browser cancels a pointer without ever
    // sending pointerup (touch turning into a scroll gesture, the OS taking the
    // input), which would otherwise leave the drag flag set and make the ROI
    // orbit on plain hover.
    const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
        event.stopPropagation();
        draggingRef.current = false;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };

    if (!isVisible) {
        return null;
    }

    return (
        <Box
            ref={rootRef}
            data-testid="roi-preview-pane"
            sx={{ position: 'relative', lineHeight: 0 }}
        >
            <canvas
                ref={canvasRef}
                style={{
                    width: sizePx,
                    height: sizePx,
                    display: 'block',
                    cursor: 'grab',
                    touchAction: 'none',
                }}
                role="img"
                aria-label={intl.formatMessage({
                    id: uncommittedVolume
                        ? 'structure.preview3D.ariaLabelUncommitted'
                        : 'structure.preview3D.ariaLabel',
                })}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
            />
            {uncommittedVolume === null &&
                committed.shownCount > 0 &&
                committed.drawableCount > committed.shownCount && (
                    <ShownCount data-testid="roi-preview-3d-shown-count">
                        <FormattedMessage
                            id="structure.preview3D.shownCount"
                            values={{
                                shown: committed.shownCount,
                                total: committed.drawableCount,
                            }}
                        />
                    </ShownCount>
                )}
        </Box>
    );
}
